import React, { useEffect, useMemo, useState } from "react";

import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CloseIcon from "@mui/icons-material/Close";

import { VariableSizeList } from "react-window";

const ALL_OPTION = {
  label: "Select All",
  value: "__all__",
};

const ALL_SELECTED_MARKER = {
  label: "All selected",
  value: "__all_selected__",
  __isAll: true,
};

const LARGE_OPTIONS_THRESHOLD = 2000;
const SEARCH_DEBOUNCE_MS = 250;
const MAX_VISIBLE_SELECTED_CHIPS = 2;
const LISTBOX_PADDING = 4;

function renderRow(props) {
  const { data, index, style } = props;
  const item = data[index];
  const inlineStyle = {
    ...style,
    top: style.top + LISTBOX_PADDING,
    width: "100%",
    boxSizing: "border-box",
    padding: "8px 8px",
    lineHeight: "1.3",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    wordBreak: "normal",
    alignItems: "center",
  };

  return React.cloneElement(item, {
    style: inlineStyle,
  });
}

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef((props, ref) => {
  const outerProps = React.useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
});

function useResetCache(dataLength) {
  const ref = React.useRef(null);
  React.useEffect(() => {
    if (ref.current != null) {
      ref.current.resetAfterIndex(0, true);
    }
  }, [dataLength]);
  return ref;
}

const ListboxComponent = React.forwardRef(function ListboxComponent(props, ref) {
  const { children, ...other } = props;
  const itemData = React.Children.toArray(children);
  const itemCount = itemData.length;
  const theme = useTheme();
  const smUp = useMediaQuery(theme.breakpoints.up("sm"));
  const itemSize = smUp ? 60 : 70;
  const gridRef = useResetCache(itemCount);

  return (
    <div ref={ref}>
      <OuterElementContext.Provider value={other}>
        <VariableSizeList
          itemData={itemData}
          height={Math.min(8, itemCount) * itemSize + 2 * LISTBOX_PADDING}
          width="100%"
          ref={gridRef}
          outerElementType={OuterElementType}
          innerElementType="ul"
          itemSize={() => itemSize}
          overscanCount={6}
          itemCount={itemCount}
          style={{ overflowX: "hidden" }}
        >
          {renderRow}
        </VariableSizeList>
      </OuterElementContext.Provider>
    </div>
  );
});

const SelectedItemsModal = ({
  open,
  onClose,
  title,
  selectedItems,
  onDelete,
}) => {
  const safeSelectedItems = useMemo(
    () => (Array.isArray(selectedItems) ? selectedItems : []),
    [selectedItems]
  );
  const thresholdForVirtual = 200; // start virtualizing for large selections
  const [selectedItemsSearch, setSelectedItemsSearch] = useState("");

  useEffect(() => {
    if (!open) {
      setSelectedItemsSearch("");
    }
  }, [open]);

  const filteredSelectedItems = useMemo(() => {
    const normalizedSearch = selectedItemsSearch.trim().toLowerCase();

    if (!normalizedSearch) {
      return safeSelectedItems;
    }

    return safeSelectedItems.filter((item) =>
      String(item?.label || item?.value || "")
        .toLowerCase()
        .includes(normalizedSearch)
    );
  }, [safeSelectedItems, selectedItemsSearch]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
      >
        {title} ({safeSelectedItems.length})

        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <TextField
          fullWidth
          size="small"
          value={selectedItemsSearch}
          onChange={(event) => setSelectedItemsSearch(event.target.value)}
          placeholder="Search selected items"
          sx={{ mb: 2 }}
        />

        {filteredSelectedItems.length === 0 ? (
          <Typography sx={{ fontSize: 13, color: "#5c6b8a" }}>
            {safeSelectedItems.length === 0
              ? "No items selected"
              : "No selected items match your search"}
          </Typography>
        ) : filteredSelectedItems.length < thresholdForVirtual ? (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {filteredSelectedItems.map((item) => (
              <Chip key={item.value} label={item.label} onDelete={() => onDelete(item)} />
            ))}
          </Box>
        ) : (
          // Virtualized list for large selections
          <Box sx={{ height: 360 }}>
            <VariableSizeList
              height={360}
              width="100%"
              itemCount={filteredSelectedItems.length}
              itemSize={() => 40}
              overscanCount={6}
              itemData={filteredSelectedItems}
            >
              {({ data, index, style }) => {
                const item = data[index];
                return (
                  <div style={{ ...style, display: "flex", alignItems: "center", padding: 6 }}>
                    <Chip
                      key={item.value}
                      label={item.label}
                      onDelete={() => onDelete(item)}
                      size="small"
                    />
                  </div>
                );
              }}
            </VariableSizeList>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default function FilterMultiSelect({
  label,
  options = [],
  value = [],
  onChange = () => {},
  disabled = false,
  loading = false,
}) {
  const [searchText, setSearchText] =
    useState("");

  const [debouncedSearchText, setDebouncedSearchText] =
    useState("");

  const [openModal, setOpenModal] =
    useState(false);

  const safeOptions = useMemo(() => (Array.isArray(options) ? options : []), [options]);

  const safeValue = useMemo(() => (Array.isArray(value) ? value : []), [value]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(searchText.trim().toLowerCase());
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [searchText]);

  const sortedOptions = useMemo(() => {
    if (safeOptions.length > LARGE_OPTIONS_THRESHOLD) {
      return safeOptions;
    }

    return [...safeOptions].sort((a, b) =>
      String(a.label || "").localeCompare(String(b.label || ""), undefined, {
        sensitivity: "base",
      })
    );
  }, [safeOptions]);

  // SEARCHED OPTIONS
  const filteredOptions = useMemo(() => {
    if (!debouncedSearchText) {
      return sortedOptions;
    }

    return sortedOptions.filter((option) =>
      String(option.label || "")
        .toLowerCase()
        .includes(debouncedSearchText)
    );
  }, [sortedOptions, debouncedSearchText]);

  const allMarker = useMemo(
    () => safeValue.find((item) => item?.__isAll),
    [safeValue]
  );

  const hasAllMarker = Boolean(allMarker);

  const excludedValueSet = useMemo(
    () => new Set(allMarker?.excludedValues || []),
    [allMarker]
  );

  const selectedValueSet = useMemo(
    () => new Set(safeValue.filter((item) => !item?.__isAll).map((item) => item.value)),
    [safeValue]
  );

  const allFilteredSelected = useMemo(() => {
    if (hasAllMarker) {
      return filteredOptions.length > 0 &&
        filteredOptions.every((option) => !excludedValueSet.has(option.value));
    }

    return filteredOptions.length > 0 &&
      filteredOptions.every((option) => selectedValueSet.has(option.value));
  }, [excludedValueSet, filteredOptions, hasAllMarker, selectedValueSet]);

  const selectedOptionCount = useMemo(() => {
    if (hasAllMarker) {
      return safeOptions.reduce(
        (count, option) => count + (excludedValueSet.has(option.value) ? 0 : 1),
        0
      );
    }

    return safeValue.filter((item) => !item?.__isAll).length;
  }, [excludedValueSet, hasAllMarker, safeOptions, safeValue]);

  const partialAllPreviewItems = useMemo(() => {
    if (!hasAllMarker || excludedValueSet.size === 0) {
      return [];
    }

    const previewItems = [];

    for (const option of safeOptions) {
      if (!excludedValueSet.has(option.value)) {
        previewItems.push(option);
      }

      if (previewItems.length === MAX_VISIBLE_SELECTED_CHIPS) {
        break;
      }
    }

    return previewItems;
  }, [excludedValueSet, hasAllMarker, safeOptions]);

  const selectedItemsForModal = useMemo(() => {
    if (!openModal) {
      return [];
    }

    if (hasAllMarker) {
      return safeOptions.filter((option) => !excludedValueSet.has(option.value));
    }

    return safeValue.filter((item) => !item?.__isAll);
  }, [excludedValueSet, hasAllMarker, openModal, safeOptions, safeValue]);

  // REMOVE CHIP
  const handleDelete = (deletedItem) => {
    if (deletedItem?.__isAll) {
      onChange([]);
      return;
    }

    const updatedValues = safeValue.filter(
      (item) =>
        item.value !== deletedItem.value
    );

    onChange(updatedValues);
  };

  const handleModalDelete = (deletedItem) => {
    if (!hasAllMarker) {
      handleDelete(deletedItem);
      return;
    }

    const nextExcludedValues = new Set(excludedValueSet);
    nextExcludedValues.add(deletedItem.value);

    const remainingCount = safeOptions.reduce(
      (count, option) => count + (nextExcludedValues.has(option.value) ? 0 : 1),
      0
    );

    if (remainingCount === 0) {
      onChange([]);
      return;
    }

    onChange([{
      ...ALL_SELECTED_MARKER,
      excludedValues: [...nextExcludedValues],
      label: `All selected (${remainingCount})`,
    }]);
  };

  const displayOptions = useMemo(() => {
    const optionMap = new Map();

    optionMap.set(ALL_OPTION.value, ALL_OPTION);

    filteredOptions.forEach((option) => {
      optionMap.set(option.value, option);
    });

    safeValue.forEach((option) => {
      if (option?.__isAll) {
        return;
      }

      optionMap.set(option.value, option);
    });

    return [...optionMap.values()];
  }, [filteredOptions, safeValue]);

  // HANDLE SELECT
  const handleChange = (
    event,
    newValue,
    reason,
    details
  ) => {
    const clickedOption = details?.option;

    // SELECT ALL
    if (clickedOption?.value === "__all__") {
      // Keep full-list selection lightweight instead of putting thousands of
      // option objects into React state.
      if (!debouncedSearchText) {
        const isEverythingSelected = hasAllMarker && excludedValueSet.size === 0;
        onChange(isEverythingSelected ? [] : [ALL_SELECTED_MARKER]);
        return;
      }

      const filteredValueSet = new Set(filteredOptions.map((option) => option.value));

      if (hasAllMarker) {
        const nextExcludedValues = new Set(excludedValueSet);

        filteredOptions.forEach((option) => {
          if (allFilteredSelected) {
            nextExcludedValues.add(option.value);
          } else {
            nextExcludedValues.delete(option.value);
          }
        });

        onChange(nextExcludedValues.size >= safeOptions.length
          ? []
          : [{
              ...ALL_SELECTED_MARKER,
              excludedValues: [...nextExcludedValues],
              label: nextExcludedValues.size > 0
                ? `All selected except ${nextExcludedValues.size}`
                : ALL_SELECTED_MARKER.label,
            }]
        );
        return;
      }

      if (allFilteredSelected) {
        onChange(
          safeValue.filter(
            (selected) => !selected?.__isAll && !filteredValueSet.has(selected.value)
          )
        );
      } else {
        const nextValueMap = new Map(
          safeValue
            .filter((selected) => !selected?.__isAll)
            .map((selected) => [selected.value, selected])
        );

        filteredOptions.forEach((option) => nextValueMap.set(option.value, option));
        onChange([...nextValueMap.values()]);
      }

      return;
    }

    if (hasAllMarker && clickedOption) {
      const nextExcludedValues = new Set(excludedValueSet);

      if (nextExcludedValues.has(clickedOption.value)) {
        nextExcludedValues.delete(clickedOption.value);
      } else {
        nextExcludedValues.add(clickedOption.value);
      }

      onChange(nextExcludedValues.size >= safeOptions.length
        ? []
        : [{
            ...ALL_SELECTED_MARKER,
            excludedValues: [...nextExcludedValues],
            label: nextExcludedValues.size > 0
              ? `All selected except ${nextExcludedValues.size}`
              : ALL_SELECTED_MARKER.label,
          }]
      );
      return;
    }

    // NORMAL SELECT
    const cleanedValues = newValue.filter(
      (item) => item.value !== "__all__"
    );

    onChange(cleanedValues);
  };

  const handlePasteFromClipboard = async () => {
    try {
      const clipboardText = await navigator.clipboard.readText();

      if (!clipboardText) return;

      const pastedValues = clipboardText
        .split(/[\n,]/)
        .map(item => item.trim())
        .filter(Boolean);

      const normalize = (str = "") =>
        str
          .toString()
          .toLowerCase()
          .replace(/\s+/g, " ")
          .trim();

      const matchedOptions = safeOptions.filter(option => {
        const optionValue = normalize(option.value);
        const optionLabel = normalize(option.label);

        const parts = option.label.split("|");

        const codePart = normalize(parts[0] || "");
        const descPart = normalize(parts.slice(1).join("|") || "");

        return pastedValues.some(pasted => {
          const searchValue = normalize(pasted);

          return (
            searchValue === optionValue ||
            searchValue === optionLabel ||
            searchValue === codePart ||
            searchValue === descPart
          );
        });
      });

      // Nothing matched -> keep existing selections
      if (matchedOptions.length === 0) {
        return;
      }

      // At least one matched -> replace existing selections
      onChange(matchedOptions);
    } catch (error) {
      console.error("Clipboard access failed", error);
    }
  };

  return (
    <>
      <Autocomplete
        ListboxComponent={ListboxComponent}
        multiple
        disabled={disabled || loading}
        disableCloseOnSelect
        options={displayOptions}
        filterOptions={(x) => x}
        popupIcon={<KeyboardArrowDownIcon />}
        value={safeValue}
        onChange={handleChange}
        inputValue={searchText}
        onInputChange={(e, value) =>
          setSearchText(value)
        }
        getOptionLabel={(option) =>
          option.label
        }
        isOptionEqualToValue={(option, val) =>
          option.value === val.value
        }
        renderOption={(props, option) => {
          const isSelectAll =
            option.value === "__all__";

          const checked = isSelectAll
            ? allFilteredSelected
            : hasAllMarker
              ? !excludedValueSet.has(option.value)
              : selectedValueSet.has(option.value);

          return (
            <li {...props} key={`${option.value}_${option.label}`} title={option.label}>
              <Checkbox checked={checked} sx={{ flexShrink: 0 }} />

              <Box
                component="span"
                sx={{
                  flex: 1,
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {option.label}
              </Box>
            </li>
          );
        }}
        renderTags={(selectedValues) => {
          if (!selectedValues || selectedValues.length === 0) {
            return null;
          }

          if (hasAllMarker && excludedValueSet.size > 0) {
            return (
              <>
                {partialAllPreviewItems.map((option) => (
                  <Chip
                    key={`${option.value}_${option.label}`}
                    label={option.label}
                    title={option.label}
                    size="small"
                    onDelete={() => handleModalDelete(option)}
                    sx={{
                      maxWidth: "150px",
                      margin: 0.1,
                    }}
                  />
                ))}

                {selectedOptionCount > MAX_VISIBLE_SELECTED_CHIPS && (
                  <Typography
                    sx={{
                      fontSize: "14px",
                      cursor: "pointer",
                      color: "#000",
                      fontWeight: 500,
                      alignSelf: "center",
                      ml: 0.5,
                    }}
                    onClick={() => setOpenModal(true)}
                  >
                    +{selectedOptionCount - MAX_VISIBLE_SELECTED_CHIPS}
                  </Typography>
                )}
              </>
            );
          }

          // Keep chip DOM bounded even for pasted or bulk selections.
          return (
            <>
              {selectedValues.slice(0, MAX_VISIBLE_SELECTED_CHIPS).map((option) => (
                <Chip
                  key={`${option.value}_${option.label}`}
                  label={option?.__isAll
                    ? `All selected (${selectedOptionCount})`
                    : option.label}
                  title={option?.__isAll
                    ? `View ${selectedOptionCount} selected items`
                    : option.label}
                  size="small"
                  onClick={option?.__isAll ? () => setOpenModal(true) : undefined}
                  onDelete={() => handleDelete(option)}
                  sx={{
                    maxWidth: "150px",
                    margin: 0.1,
                    cursor: option?.__isAll ? "pointer" : "default",
                  }}
                />
              ))}

              {selectedValues.length > MAX_VISIBLE_SELECTED_CHIPS && (
                <Typography
                  sx={{
                    fontSize: "14px",
                    cursor: "pointer",
                    color: "#000",
                    fontWeight: 500,
                    alignSelf: "center",
                    ml: 0.5,
                  }}
                  onClick={() => setOpenModal(true)}
                >
                  +{selectedValues.length - MAX_VISIBLE_SELECTED_CHIPS}
                </Typography>
              )}
            </>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder="Search"
            size="small"
            style={{ fontSize: "11px" }}
          />
        )}
        sx={{
          "& .MuiOutlinedInput-root": {
            minHeight: "32px",
            background: "#fff",
            alignItems: "flex-start",
            pt: 0.5,
          },

          "& .MuiAutocomplete-tag": {
            margin: "2px",
          },
        }}
      />

      <Typography
        sx={{
          fontSize: "9px",
          color: (disabled || loading) ? "#a0a8b8" : "#5c6b8a",
          textAlign: "right",
          mt: 0.5,
          mr: 1,
          cursor: (disabled || loading) ? "not-allowed" : "pointer",
        }}
        onClick={(disabled || loading) ? undefined : handlePasteFromClipboard}
      >
        PASTE FROM CLIPBOARD
      </Typography>

      {/* SELECTED ITEMS MODAL */}

      <SelectedItemsModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        title={label}
        selectedItems={selectedItemsForModal}
        onDelete={handleModalDelete}
      />
    </>
  );
}
